import AtaDetailsPrint from '@/components/ata/ata-details-print.vue';
import { RouteName } from '@/router/models/route-name';
import { authHelper } from '@/services/auth/auth-helper';
import { entraAuthService } from '@/services/auth/entra-auth-service';
import AdminCreateInvoiceManagement from '@/views/admin/AdminCreateInvoiceManagement.vue';
import AdminDocumentManagement from '@/views/admin/AdminDocumentManagement.vue';
import AdminEditInvoiceManagement from '@/views/admin/AdminEditInvoiceManagement.vue';
import AdminProjectManagement from '@/views/admin/AdminProjectManagement.vue';
import AdminSupplierInvoiceManagement from '@/views/admin/AdminSupplierInvoiceManagement.vue';
import AdminSupplierManagement from '@/views/admin/AdminSupplierManagement.vue';
import AdminPage from '@/views/AdminPage.vue';
import AtaDetailsPage from '@/views/ata/ata-details-page.vue';
import ATAPage from '@/views/ata/ATAPage.vue';
import AuthTestpage from '@/views/AuthTestpage.vue';
import CalculateBudgetPage from '@/views/CalculateBudgetPage.vue';
import DocumentsPage from '@/views/DocumentsPage.vue';
import ForecastPage from '@/views/ForecastPage.vue';
import InvoiceDetailsPage from '@/views/invoice/invoice-details-page.vue';
import InvoicePage from '@/views/invoice/invoice-page.vue';
import OverviewPage from '@/views/OverviewPage.vue';
import ProjectDetailPage from '@/views/ProjectDetailPage.vue';
import ProjectPage from '@/views/ProjectPage.vue';
import { models } from 'cesg-shared';
import { PermissionRouteName } from 'cesg-shared/dist/models';
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router';
import StartPage from '../views/StartPage.vue';

type Access = models.Access;

type AppRouteRecord = {
  meta: {
    /**
     * defaults to true
     */
    requiresAuthentication: boolean;
    isAdmin: boolean;
    /**
     * defaults to [] which equals that no special access is needed.
     */
    requiresAccess?: Access[];
  };
} & RouteRecordRaw;

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: RouteName.StartPage,
      component: StartPage,
      meta: {
        requiresAuthentication: false,
      },
    },
    {
      path: `/${RouteName.Projects}`,
      name: RouteName.Projects,
      component: ProjectPage,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: `/${RouteName.Admin}`,
      name: RouteName.Admin,
      component: AdminPage,
      meta: {
        requiresAuthentication: true,
        isAdmin: true,
      },
      children: [
        {
          path: RouteName.AdminProjectAndUsers,
          name: RouteName.AdminProjectAndUsers,
          component: AdminProjectManagement,
        },
        {
          path: RouteName.AdminSupplierInvoices,
          name: RouteName.AdminSupplierInvoices,
          component: AdminSupplierInvoiceManagement,
        },
        {
          path: RouteName.AdminSuppliers,
          name: RouteName.AdminSuppliers,
          component: AdminSupplierManagement,
        },
        {
          path: RouteName.AdminDocuments,
          name: RouteName.AdminDocuments,
          component: AdminDocumentManagement,
        },
        {
          path: RouteName.AdminCreateInvoices,
          name: RouteName.AdminCreateInvoices,
          component: AdminCreateInvoiceManagement,
        },
        {
          path: `/${RouteName.AdminEditInvoice}/:id`,
          name: RouteName.AdminEditInvoice,
          component: AdminEditInvoiceManagement,
          props: true,
        },
        {
          path: 'authtest',
          name: 'authtest',
          component: AuthTestpage,
        },
      ],
    },
    {
      path: `/${RouteName.Projects}/:id`,
      component: ProjectDetailPage,
      meta: {
        requiresAuthentication: true,
      },
      children: [
        {
          path: '',
          redirect: (to) => {
            return { name: RouteName.Overview, id: to.params.id };
          },
        },
        {
          path: RouteName.Overview,
          name: RouteName.Overview,
          component: OverviewPage,
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: RouteName.CalculationBudget,
          name: RouteName.CalculationBudget,
          component: CalculateBudgetPage,
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: RouteName.Forecast,
          name: RouteName.Forecast,
          component: ForecastPage,
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: RouteName.Documents,
          name: RouteName.Documents,
          component: DocumentsPage,
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: RouteName.ATA,
          name: RouteName.ATA,
          component: ATAPage,
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: `${RouteName.ATA}/:ataId`,
          name: RouteName.ATADetails,
          components: {
            default: AtaDetailsPage,
          },
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: `${RouteName.ATA}/print/:ataId`,
          name: RouteName.ATADetailsPrint,
          components: {
            default: AtaDetailsPrint,
          },
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: RouteName.Invoices,
          name: RouteName.Invoices,
          component: InvoicePage,
          meta: {
            requiresAuthentication: true,
          },
        },
        {
          path: `${RouteName.Invoices}/:invoiceId`,
          name: RouteName.InvoiceDetails,
          components: {
            default: InvoiceDetailsPage,
          },
          meta: {
            requiresAuthentication: true,
          },
        },
      ],
    },
  ],
  scrollBehavior() {
    return { top: 0, behavior: 'instant' };
  },
});

router.beforeEach((to, from, next) => {
  const authenticated = entraAuthService.isLoggedIn.value;
  const authenticatedUser = entraAuthService.me.value;
  const meta = to.meta as AppRouteRecord['meta'];
  const permissionRouteName = authHelper.convertToPermissionRouteName(
    to.name?.toString(),
  );

  if (!meta.requiresAuthentication) {
    return next();
  }

  if (authenticated && authenticatedUser) {
    if (
      permissionRouteName === PermissionRouteName.PROJECTS ||
      permissionRouteName === PermissionRouteName.STARTPAGE ||
      permissionRouteName === PermissionRouteName.ADMIN
    ) {
      if (
        authHelper.authenticateUserRoutePermission(
          authenticatedUser,
          permissionRouteName,
        )
      ) {
        return next();
      }
    } else {
      const projectId = to.params.id.toString();

      if (
        authHelper.authenticateProjectRoutePermission(
          authenticatedUser,
          projectId,
          permissionRouteName,
        )
      ) {
        return next();
      }
    }
    return next({ name: from.name });
  }

  return next({ name: RouteName.StartPage });
});

export default router;
